import r from '../Utils/fetch';

const w: any = window;
const API_URL = w.API_MCP_PUBLIC_URL;

// =============================================================================
// TYPES
// =============================================================================

/**
 * Statuts d'une instance LLM — miroir de models.LlmStatus (packages/go/sdk/models/llm.model.go).
 * En pratique le provisioner n'écrit que `creating`, `active` et `error` ; `pending` est
 * le défaut de la colonne et `deleted` n'est jamais posé (Destroy fait un hard delete).
 */
export type LlmStatus = 'pending' | 'creating' | 'active' | 'error' | 'deleted';

export interface LlmInstance {
	id: number;
	oauthClientId: string;
	projectId: number;
	name: string;
	slug: string;
	domain: string;
	status: LlmStatus;
	errorMessage?: string;
	region: string;
	llmProvider: string;
	llmModel: string;
	llmBaseUrl: string;
	publicIp?: string;
	instanceId?: string;
	adminPassword?: string;
	primaryColor: string;
	secondaryColor: string;
	createdAt: string;
	updatedAt: string;
}

export interface ChatStatusResponse {
	llm: LlmInstance;
	project: {
		id: number;
		name: string;
		folderId: string;
	};
}

/**
 * Vue "liste" d'une instance LLM (GET /admin/chat/instances).
 * Volontairement sans `adminPassword` : le backend ne le renvoie pas sur la liste
 * pour éviter d'expédier tous les mots de passe admin dans un seul payload. Il
 * reste disponible sur l'écran détail d'un projet (getChatStatus).
 */
export interface LlmInstanceListItem {
	id: number;
	projectId: number;
	projectName: string;
	projectFolderId: string;
	name: string;
	slug: string;
	domain: string;
	status: LlmStatus;
	errorMessage?: string | null;
	region: string;
	llmProvider: string;
	llmModel: string;
	publicIp?: string | null;
	instanceId?: string | null;
	createdAt: string;
}

export interface ChatInstancesListResponse {
	instances: LlmInstanceListItem[];
	total: number;
}

// =============================================================================
// API CALLS
// =============================================================================

export const listChatInstances = (): Promise<ChatInstancesListResponse> =>
	r({
		url: `${API_URL}/admin/chat/instances`,
		method: 'GET',
	});

export const getChatStatus = (projectId: number): Promise<ChatStatusResponse> =>
	r({
		url: `${API_URL}/admin/chat/instances/${projectId}`,
		method: 'GET',
	});

export const createChatInstance = (projectId: number): Promise<{ llm: LlmInstance }> =>
	r({
		url: `${API_URL}/admin/chat/instances`,
		method: 'POST',
		data: { projectId },
	});

export const destroyChatInstance = (projectId: number): Promise<{ message: string }> =>
	r({
		url: `${API_URL}/admin/chat/instances/${projectId}`,
		method: 'DELETE',
	});

export interface UserUsage {
	userEmail: string;
	promptTokens: number;
	completionTokens: number;
	totalTokens: number;
	requestCount: number;
}

export interface ChatUsageResponse {
	month: string;
	usage: UserUsage[];
}

export const getChatUsage = (projectId: number, month?: string): Promise<ChatUsageResponse> =>
	r({
		url: `${API_URL}/admin/chat/instances/${projectId}/usage${month ? `?month=${month}` : ''}`,
		method: 'GET',
	});
